export const PRODUCT_OVERVIEW = {
  title: 'Product Overview',
  intro:
    'SkoolWeb is a cloud-based school management platform built for K–12 schools, colleges, and multi-campus institutions. It brings admissions, academics, finance, operations, and parent communication into one secure system — so administrators, teachers, parents, and students work from a single source of truth.',
  highlights: [
    { label: 'Multi-school ready', detail: 'Each school runs on its own isolated database with institute-number login.' },
    { label: 'Role-based access', detail: 'Headmaster, teacher, student, parent, and staff see only what they need.' },
    { label: 'Real-time dashboards', detail: 'Live counts for students, fees, attendance, and notices on day one.' },
    { label: 'SaaS module control', detail: 'Enable or hide modules per school subscription plan.' },
  ],
  moduleNames: [
    'Student Management',
    'Admission Management',
    'Attendance Tracking',
    'Fees Management',
    'Examination System',
    'Assemblies Management',
    'Student Groups',
    'Timetable Management',
    'Homework & Assignments',
    'Meetings Management',
    'Payroll Management',
    'Parent Portal',
    'Teacher Portal',
    'Transport Management',
    'Library Management',
    'Hostel Management',
  ],
};

export const GETTING_STARTED = {
  title: 'Getting Started',
  intro:
    'Follow this setup sequence when onboarding a new school on SkoolWeb. Each step builds on the previous one — complete them in order before day-to-day operations like attendance, exams, or fee collection.',
  recommendedOrder: [
    'Creating a school account',
    'Adding academic sessions',
    'Creating classes and sections',
    'Adding teachers',
    'Importing students',
    'Configuring fee structures',
  ],
  guides: [
    {
      id: 'creating-school-account',
      stepNumber: 1,
      title: 'Creating a School Account',
      icon: '🏫',
      accent: '#667eea',
      summary:
        'Subscribe to SkoolWeb, receive your institute number and admin login, and access your isolated school workspace.',
      sidebarPath: 'Login page → Headmaster dashboard',
      prerequisites: ['SkoolWeb subscription or trial approved', 'Primary contact email and phone for the school'],
      steps: [
        {
          heading: 'Subscribe and register your school',
          points: [
            'Choose a plan on the SkoolWeb website or contact the Futuristic5 team to start a trial.',
            'Provide school name, type (K–12, college, etc.), city, and expected student count.',
            'The platform team registers your school in the central registry and provisions a dedicated PostgreSQL database — fully isolated from other schools.',
          ],
        },
        {
          heading: 'Receive your login credentials',
          points: [
            'You receive a unique Institute Number (e.g. a 4-digit code) that identifies your school at login.',
            'A Headmaster / Admin username and temporary password are shared securely with your school contact.',
            'Each school’s data lives in its own database; no other school can see your students, fees, or records.',
          ],
        },
        {
          heading: 'First login',
          points: [
            'Open the SkoolWeb login page and enter Institute Number, Username, and Password.',
            'The system resolves your institute number to the correct school database and verifies credentials.',
            'On success you land on the Headmaster Dashboard with live widgets for students, teachers, fees, and notices.',
            'Change the default password from Profile or Settings after first login.',
          ],
        },
        {
          heading: 'Verify your workspace',
          points: [
            'Confirm the school name appears in the header or profile area.',
            'Review which modules are enabled for your subscription plan in the sidebar.',
            'Proceed to Academic Years to set up your first session before adding classes or students.',
          ],
        },
      ],
      tips: [
        'Keep the institute number handy — every user at your school enters it at login along with their username.',
        'Teachers, parents, and students use the same login page; only the institute number and role differ.',
        'If login fails with “invalid institute number”, double-check the code provided at signup — not another school’s number.',
      ],
      relatedModuleIds: ['student-management'],
    },
    {
      id: 'adding-academic-sessions',
      stepNumber: 2,
      title: 'Adding Academic Sessions',
      icon: '📅',
      accent: '#764ba2',
      summary:
        'Define academic years (sessions) with start and end dates — the foundation for classes, attendance, fees, and exams.',
      sidebarPath: 'Academic → Academic Years',
      prerequisites: ['Headmaster / Admin login', 'School account active'],
      steps: [
        {
          heading: 'Open Academic Years',
          points: [
            'Go to Academic → Academic Years from the sidebar.',
            'The list shows every session with year name, start date, end date, and current/active status.',
            'Use search to find a specific year when you have multiple sessions on record.',
          ],
        },
        {
          heading: 'Create your first academic year',
          points: [
            'Click Add Academic Year (or open the create form).',
            'Enter Year Name (e.g. “2025–26” or “Academic Year 2025–26”).',
            'Set Start Date and End Date — both are required and define when the session is valid.',
            'Mark Is Current if this is the active session; mark Is Active to keep it selectable across the app.',
            'Save — the new year appears in the list and in the academic year selector in the page header.',
          ],
        },
        {
          heading: 'Clone structure from a previous year (optional)',
          points: [
            'When adding a second year onward, you can copy data from the previous session.',
            'Optional clone modules: Classes, Sections, Subjects, Teacher Assignments, and Timetable.',
            'Dependencies apply — e.g. cloning Sections requires Classes; cloning Timetable requires Classes, Sections, and Subjects.',
            'The previous year must have an end date set before the new year’s start date can begin.',
          ],
        },
        {
          heading: 'Switch the working session',
          points: [
            'Use the Academic Year selector in the top header to switch between sessions.',
            'All academic screens — classes, attendance, fees, exams — respect the selected year.',
            'When a session closes, set its end date and mark a new year as current.',
          ],
        },
      ],
      tips: [
        'Always select the correct academic year in the header before importing students or configuring fees.',
        'Cloning saves hours at year-end — use it to carry forward class structure without re-entering everything.',
        'If timetable clone fails due to overlap, resolve conflicts in the old session’s timetable first.',
      ],
      relatedModuleIds: ['timetable-management', 'examination-system'],
    },
    {
      id: 'creating-classes-sections',
      stepNumber: 3,
      title: 'Creating Classes and Sections',
      icon: '🏛️',
      accent: '#3b82f6',
      summary:
        'Build your academic structure — classes (grades), section names, class–section links, and optional classrooms.',
      sidebarPath: 'Academic → Classes',
      prerequisites: ['At least one academic year created and selected in the header'],
      steps: [
        {
          heading: 'Add classrooms (optional but recommended)',
          points: [
            'Go to Academic → Classes → Class Room.',
            'Register physical rooms with name/number and capacity.',
            'Rooms can be assigned when linking sections to classes.',
          ],
        },
        {
          heading: 'Create classes (grades)',
          points: [
            'Open Academic → Classes → Classes.',
            'Click Add and enter Class Name (e.g. “Class X”, “Grade 5”).',
            'Set Class Code (short code used in reports and roll numbers).',
            'Set Max Students and Description; mark Active status.',
            'Save — the class appears in the classes table with section count and subject count.',
          ],
        },
        {
          heading: 'Define section names',
          points: [
            'Go to Academic → Classes → Section.',
            'Add sections such as A, B, C — section names are reused across classes.',
            'Each section can have a description; inactive sections can be hidden from new assignments.',
          ],
        },
        {
          heading: 'Link class + section for the academic year',
          points: [
            'Open Academic → Classes → Class Sections.',
            'Create a class–section pair for the selected academic year.',
            'Assign Max Students, optional Class Room, and Active status per link.',
            'Assign a Class Teacher from your teacher list where applicable.',
          ],
        },
        {
          heading: 'Add subjects to classes',
          points: [
            'Go to Academic → Subjects → Subject List to define subjects (Math, English, etc.).',
            'Open Class Subjects to map subjects to each class for the current academic year.',
            'Subjects are required before teacher assignments, timetable, homework, and exams.',
          ],
        },
      ],
      tips: [
        'Complete classes and sections before importing students — import requires a target class and section.',
        'Use consistent class codes (I, II, X, XII) — they appear in admission numbers and reports.',
        'You can assign class teachers later, but doing it during setup helps teachers see their classes immediately.',
      ],
      relatedModuleIds: ['student-management', 'timetable-management'],
    },
    {
      id: 'adding-teachers',
      stepNumber: 4,
      title: 'Adding Teachers',
      icon: '👩‍🏫',
      accent: '#06b6d4',
      summary:
        'Register teaching staff with login credentials, department, designation, and optional class–subject assignments.',
      sidebarPath: 'People → Teachers → All Teachers → Add Teacher',
      prerequisites: ['Academic year selected', 'Classes and subjects configured (for assignments)'],
      steps: [
        {
          heading: 'Open the Add Teacher form',
          points: [
            'Navigate to People → Teachers → All Teachers.',
            'Click Add Teacher to open the full registration form.',
            'The same form is used for editing existing teachers later.',
          ],
        },
        {
          heading: 'Personal information',
          points: [
            'Enter First Name, Last Name, Gender, Date of Birth, and contact details (phone, email, address).',
            'Upload a profile photo and supporting documents if required by your school policy.',
            'Blood group and other demographic fields are optional but available.',
          ],
        },
        {
          heading: 'Employment and login',
          points: [
            'Select Department and Designation (e.g. Academic → Senior Teacher). Designation options filter by department.',
            'Set Join Date, Employee ID, and employment status.',
            'Create Username and Password — these are the teacher’s portal login credentials (with your institute number).',
            'Username uniqueness is validated before save.',
          ],
        },
        {
          heading: 'Payroll and documents (optional)',
          points: [
            'Configure salary components and bank details if your school uses the HR/payroll module.',
            'Attach contract or qualification documents for records.',
          ],
        },
        {
          heading: 'Assign classes and subjects',
          points: [
            'After saving, open People → Teachers → Teacher Assignments.',
            'Link the teacher to class, section, and subject for the current academic year.',
            'Assignments control which classes appear on the teacher dashboard, attendance screen, and exam marks entry.',
            'View the teacher’s weekly routine under Teacher Timetable once the timetable is built.',
          ],
        },
      ],
      tips: [
        'Add teachers before or in parallel with student import — class teachers should exist before assigning sections.',
        'Teachers see a focused portal: dashboard, attendance, homework, exams, and their class students only.',
        'Share institute number + username + password securely; teachers change password after first login.',
      ],
      relatedModuleIds: ['teacher-portal', 'attendance-tracking'],
    },
    {
      id: 'importing-students',
      stepNumber: 5,
      title: 'Importing Students',
      icon: '📥',
      accent: '#10b981',
      summary:
        'Bulk-enrol students from Excel or CSV with validation preview — faster than adding one-by-one at term start.',
      sidebarPath: 'People → Students → Students List → Import Students',
      prerequisites: [
        'Academic year selected in header',
        'Target class and section created under Class Sections',
      ],
      steps: [
        {
          heading: 'Open the import dialog',
          points: [
            'Go to People → Students → Students List (or All Students).',
            'Click Import Students in the toolbar.',
            'The import modal opens — class, section, and academic year are chosen here, not inside the file.',
          ],
        },
        {
          heading: 'Select class and section',
          points: [
            'Choose Class from the dropdown (required).',
            'Choose Section — required when the class has sections configured.',
            'Academic year is taken automatically from the header selector; verify it is correct before importing.',
          ],
        },
        {
          heading: 'Download and fill the template',
          points: [
            'Click Download sample Excel template — it includes a Guide sheet and a Data sheet.',
            'Required columns per row: admission_number, first_name, last_name, phone.',
            'Optional columns include roll_number, gender, date_of_birth, email, parent/guardian details, addresses, and lookup fields (blood group, religion, house, etc.).',
            'Remove or replace the demo row on the Data sheet before upload.',
          ],
        },
        {
          heading: 'Upload and review preview',
          points: [
            'Upload .xlsx, .xls, or .csv file.',
            'The system parses rows and shows a Data Preview with valid vs. error rows.',
            'Fix validation errors in your file (duplicate admission numbers, invalid phone/email, missing required fields) and re-upload if needed.',
          ],
        },
        {
          heading: 'Run import',
          points: [
            'Click Import when at least one row is valid.',
            'The system creates student records, guardian/parent links, and portal login accounts where configured.',
            'Results show created count, failed count, and per-row error messages.',
            'Imported students appear immediately in Students List for the selected class and section.',
          ],
        },
      ],
      tips: [
        'Import one class–section at a time — run separate imports for Class X-A and Class X-B.',
        'Admission numbers must be unique within the school; duplicates in the file or database are rejected.',
        'For single admissions with full detail, use People → Students → Add Student instead of bulk import.',
        'Father, mother, and student may share the same phone number — validation allows this.',
      ],
      relatedModuleIds: ['student-management', 'admission-management', 'parent-portal'],
    },
    {
      id: 'configuring-fee-structures',
      stepNumber: 6,
      title: 'Configuring Fee Structures',
      icon: '💰',
      accent: '#f59e0b',
      summary:
        'Set up fee taxonomy, class-wise configurations, installments, discounts, assign charges to students, and start collecting payments.',
      sidebarPath: 'Settings → Fee Settings · Management → Fees Collection',
      prerequisites: [
        'Academic year selected in the header',
        'Classes and sections created',
        'Payment modes configured (Settings → Payment Modes)',
      ],
      steps: [
        {
          heading: 'Step 1 — Payment modes & fee taxonomy',
          points: [
            'Go to Settings → Payment Modes and add modes used at your counter (Cash, UPI, Cheque, Bank Transfer, etc.).',
            'Open Settings → Fee Settings → Fee Categories — create categories such as Academic, Transport, Hostel, Library.',
            'Open Fee Heads — link each head to a category (e.g. Tuition, Development, Exam under Academic).',
            'Mark heads as optional where charges apply only to some students.',
          ],
        },
        {
          heading: 'Step 2 — Fee types & class configurations',
          points: [
            'Settings → Fee Settings → Fees Type — add global fee type names (Tuition, Transport, Library, etc.).',
            'Management → Fees Collection → Fees Group — create a fee configuration per class (or all classes at once).',
            'Attach multiple fee line items with amount per item; set due date and late-fee rules (fixed, percentage, or none).',
            'Optional: open Manage Installments on a configuration to split the total into monthly or term-wise installments.',
          ],
        },
        {
          heading: 'Step 3 — Fees master & discount rules',
          points: [
            'Settings → Fee Settings → Fees Master — review or fine-tune individual fee lines (group + type + amount + due + fine).',
            'Management → Fees Collection → Discount Rules — create percentage, fixed, or full-waiver rules for scholarships.',
            'Assign a discount rule to specific students when needed.',
          ],
        },
        {
          heading: 'Step 4 — Assign & sync student charges',
          points: [
            'Management → Fees Collection → Assign / Sync — bulk-assign fee master lines to students filtered by class and section.',
            'Use Re-sync on a student to rebuild charges and toggle optional fee groups on or off.',
            'After sync, each student has an open fee ledger (charges) for the academic year.',
          ],
        },
        {
          heading: 'Step 5 — Collect fees & receipts',
          points: [
            'Management → Fees Collection → Collect Fees — search student; view aging buckets and balance status.',
            'Click Collect Fees on a row — select one or more outstanding lines, apply wallet credit if available, record payment mode and date.',
            'System generates a PDF receipt with gross, discount, waiver, and fine breakdown.',
            'Parents and students see dues under Academic → Fees; they can view history and pay online where enabled.',
          ],
        },
        {
          heading: 'Step 6 — Reports & reconciliation',
          points: [
            'Report → Fees Report — student-level due, paid, and balance by class.',
            'Report → Fee Engine Insights — analytics tabs for collections, defaulters, installments, head-wise totals, and daily collection.',
            'Management → Fees Collection → Receipt History — reprint or void a receipt if entered incorrectly.',
          ],
        },
      ],
      tips: [
        'Complete fee setup before admission season so enquiry follow-ups can share accurate class-wise fee totals.',
        'Always verify the academic year selector in the header before configuring fees or collecting payments.',
        'Use Assign / Sync after changing fee configurations so existing students receive updated charges.',
        'Run Fee Engine Insights → Run Jobs periodically to apply late fees and refresh summary totals.',
      ],
      relatedModuleIds: ['fees-management', 'parent-portal', 'transport-management'],
    },
  ],
};

export const DOCUMENTATION_MODULES = [
  {
    id: 'student-management',
    title: 'Student Management',
    icon: '👨‍🎓',
    accent: '#667eea',
    summary: 'Central student records — from admission to promotion, with full academic and personal profiles.',
    purpose:
      'Maintain a complete digital record for every student including class, section, parent links, documents, and lifecycle status (active, promoted, left, rejoined).',
    features: [
      {
        heading: 'Student directory',
        points: [
          'Grid and list views with search, filters by class, section, and academic year.',
          'Admission number, roll number, gender, contact, and address on one profile.',
          'Photo and document attachments on student records.',
        ],
      },
      {
        heading: 'Student profile & sub-records',
        points: [
          'Drill-down tabs for attendance history, exam results, fees, leaves, and timetable.',
          'Linked parent and guardian accounts for portal access.',
          'Bonafide certificate generation in PDF format.',
        ],
      },
      {
        heading: 'Lifecycle management',
        points: [
          'Add and edit students with validation for duplicate admission numbers.',
          'Student promotion wizard to move entire classes to the next academic year.',
          'Leave school and rejoin workflows with audit trail.',
        ],
      },
    ],
    workflow: [
      'Go to Peoples → Students → Students List.',
      'Click Add Student and complete admission details, class, section, and parent link.',
      'Open a student row to view attendance, fees, exams, and documents.',
      'Use Student Promotion at year-end to advance students to the next class.',
    ],
    roles: ['Headmaster / Admin', 'Administrative staff', 'Teachers (class-scoped view)'],
  },
  {
    id: 'admission-management',
    title: 'Admission Management',
    icon: '📝',
    accent: '#764ba2',
    summary: 'Track admission enquiries from first contact through follow-up to enrolled student.',
    purpose:
      'Give admissions teams a structured pipeline — capture leads, schedule follow-ups, and convert successful enquiries into live student records without re-entering data.',
    features: [
      {
        heading: 'Admission enquiries',
        points: [
          'Enquiry list with status, source, class interested, and contact details.',
          'Add follow-up notes and next-action dates on each enquiry.',
          'Filter and search enquiries by date, status, or class.',
        ],
      },
      {
        heading: 'Convert to student',
        points: [
          'One-click conversion from enquiry to new student admission form.',
          'Pre-fill name, phone, and class preference from enquiry data.',
          'Track which enquiries converted vs. dropped off.',
        ],
      },
      {
        heading: 'Admission records',
        points: [
          'Full student registration with institute admission number and roll assignment.',
          'Assign class, section, house, religion, and transport/hostel if applicable.',
          'Generate login credentials for student and linked parent accounts.',
        ],
      },
    ],
    workflow: [
      'Open Academic → Enquiries to log a new admission lead.',
      'Add follow-up calls and update enquiry status (New, In Progress, Converted, Closed).',
      'When ready, convert the enquiry to Add Student and complete registration.',
      'Assign fees structure and notify parent via portal login.',
    ],
    roles: ['Headmaster / Admin', 'Administrative staff', 'Admissions desk'],
  },
  {
    id: 'attendance-tracking',
    title: 'Attendance Tracking',
    icon: '✅',
    accent: '#10b981',
    summary: 'Daily student and staff attendance with reports, leave integration, and portal visibility.',
    purpose:
      'Replace paper registers with digital attendance marking, automatic percentage calculation, and role-scoped views for teachers, parents, and administrators.',
    features: [
      {
        heading: 'Student attendance',
        points: [
          'Mark Present / Absent / Late by class and date from HRM → Student Attendance.',
          'Teachers mark only their assigned classes; admins mark school-wide.',
          'Bulk marking for an entire class in one screen.',
        ],
      },
      {
        heading: 'Staff attendance',
        points: [
          'Separate staff attendance register for non-teaching employees.',
          'Monthly and day-wise staff attendance reports.',
        ],
      },
      {
        heading: 'Leave & attendance link',
        points: [
          'Students and staff apply for leave from their portal.',
          'Approvers review requests under HRM → Approve Request.',
          'Approved leave reflects in attendance reports automatically.',
        ],
      },
      {
        heading: 'Reports & dashboards',
        points: [
          'Attendance percentage on student, parent, and admin dashboards.',
          'Attendance Report, Class Report, and daily attendance exports.',
          'Filter by academic year, class, section, and date range.',
        ],
      },
    ],
    workflow: [
      'Teacher opens Academic → Attendance → Student Attendance.',
      'Select class, section, and today’s date.',
      'Mark each student Present or Absent and save.',
      'Parents and students view history under Leave & Attendance in their portal.',
    ],
    roles: ['Teachers', 'Headmaster / Admin', 'Students & Parents (view only)'],
  },
  {
    id: 'fees-management',
    title: 'Fees Management',
    icon: '💰',
    accent: '#f59e0b',
    summary:
      'Full fee engine — taxonomy, class configurations, installments, discounts, wallet, collection, receipts, voiding, and analytics.',
    purpose:
      'SkoolWeb Fees Management runs on a layered Fee Engine. Schools define categories and heads, build class-wise fee configurations, assign charges to students, collect partial or full payments (with wallet and discounts), print PDF receipts, and analyse collections through dedicated reports — all synced to student profiles and parent/student portals.',
    features: [
      {
        heading: 'Fee taxonomy & settings',
        points: [
          'Settings → Payment Modes — Cash, UPI, Cheque, Bank Transfer, and custom modes for collection.',
          'Settings → Fee Settings → Fee Categories — group heads (Academic, Transport, Hostel, Library).',
          'Fee Heads — codes linked to categories; optional flag for elective charges.',
          'Fees Type — global catalog of fee names reused across classes.',
          'Fees Master — per-year line items with group, type, amount, due date, and late-fee rules.',
        ],
      },
      {
        heading: 'Fee configurations & installments',
        points: [
          'Fees Group — class-wise fee configurations with multiple line items, compulsory vs optional totals.',
          'Late fee rules: fixed amount, percentage, or none — with frequency settings.',
          'Manage Installments — split a configuration into equal monthly/term installments (1–24).',
          'Export configurations to Excel or PDF for management approval.',
        ],
      },
      {
        heading: 'Discounts, assignment & sync',
        points: [
          'Discount Rules — percentage, fixed amount, or full waiver; applies to all charges, fee head, category, or student.',
          'Assign rule to individual students with validity date range.',
          'Assign / Sync — bulk assign fee lines to students by class and section.',
          'Per-student Re-sync — rebuild charges and toggle optional fee groups.',
        ],
      },
      {
        heading: 'Collection & wallet',
        points: [
          'People → Students → Student Details → Fees — per-student ledger, history, and receipt print for admin staff.',
          'Collect Fees — summary cards: total demanded, collected, outstanding, defaulters, aging buckets (current, 1–30, 31–60, 61–90, 90+ days).',
          'Multi-line partial payment across outstanding charges in one receipt.',
          'Apply student wallet balance at collection time.',
          'Wallet screen — manual credit, balance view, transaction history.',
          'Status badges: Paid, Partial, Unpaid, No Fees.',
        ],
      },
      {
        heading: 'Adjustments, receipts & voiding',
        points: [
          'Adjustments log — discounts, waivers, and fines applied to open charges.',
          'Manual waiver workflow: select student → open charge → amount → reason.',
          'Receipt History — filter by date and status; reprint PDF receipts.',
          'Void confirmed receipts — reverses allocations and restores wallet; optional void reason recorded.',
        ],
      },
      {
        heading: 'Reports & analytics',
        points: [
          'Fees Report — per-student due, paid, balance, last payment, and status by class/section.',
          'Fee Engine Insights — 7 tabs: Outstanding by Module, Collections, Installment Compliance, Head Wise, Defaulters, Discounts & Waivers, Daily Collection.',
          'Run Jobs button — triggers late-fee accrual and summary rebuild.',
          'Admin dashboard widgets — fee collection chart, outstanding, fines, defaulter count.',
        ],
      },
      {
        heading: 'Parent, student & transport',
        points: [
          'Student / Parent portal → Academic → Fees — ledger, payment history, print receipts.',
          'Pay Online option for parent, guardian, and student roles (where payment gateway is configured).',
          'Transport Fees — pickup-point plans feed transport charges into the same fee engine.',
          'Multi-module charge badges: Academic, Transport, Library, Hostel on student fee ledger.',
        ],
      },
    ],
    workflow: [
      'Settings → Payment Modes — add collection modes.',
      'Settings → Fee Settings → Fee Categories → Fee Heads → Fees Type.',
      'Management → Fees Collection → Fees Group — build class configurations (+ optional Installments).',
      'Settings → Fee Settings → Fees Master — verify line items.',
      'Management → Fees Collection → Discount Rules — create scholarship rules.',
      'Management → Fees Collection → Assign / Sync — generate student charges.',
      'Management → Fees Collection → Collect Fees — record payments and print receipts.',
      'Management → Fees Collection → Wallet / Adjustments / Receipt History — manage credits and corrections.',
      'Report → Fees Report & Fee Engine Insights — reconcile and analyse.',
    ],
    roles: [
      'Headmaster / Admin',
      'Accounts / Administrative staff',
      'Parents & Students (view dues, pay online, print receipts)',
    ],
  },
  {
    id: 'examination-system',
    title: 'Examination System',
    icon: '📋',
    accent: '#ef4444',
    summary:
      'Offline and online exams, optional internal assessment, question bank, papers, evaluation, grades, and top performers.',
    purpose:
      'SkoolWeb Examinations covers the full cycle — exam types, class exams, subject timetable (Offline or Online), optional Internal Assessment (theory + internal marks), question bank/papers for online delivery, teacher evaluation, published results, grade scales, and top-performer analytics for students and parents.',
    features: [
      {
        heading: 'Exam types & exams',
        points: [
          'Academic → Examinations → Exam Types — Unit Test, Mid-Term, Final, etc.',
          'Exams — create exam for academic year, select classes, publish/draft.',
          'Optional Has Internal Assessment toggle on the exam (theory + internal marks).',
        ],
      },
      {
        heading: 'Schedule — Offline vs Online',
        points: [
          'From Exams → Manage / Timetable Setup — set date, time, room, max/pass marks per subject.',
          'Exam mode Offline (classroom) or Online (requires question paper).',
          'Schedule view: Academic → Examinations → Schedule (timetable).',
          'Internal ON adds Internal Max / Pass and pass policy (total only or pass both parts).',
        ],
      },
      {
        heading: 'Internal Assessment',
        points: [
          'Not a separate module — enabled per exam via Has Internal Assessment.',
          'Marks entry uses Theory and Internal columns; total = theory + internal.',
          'Pass rules: total only, or need to pass both theory and internal thresholds.',
          'Works with Offline or Online subject slots (online score maps to theory when Internal is on).',
        ],
      },
      {
        heading: 'Online exams — bank, papers, attempts',
        points: [
          'Question Bank — create/import MCQ, True/False, Fill blank, Short/Long answer.',
          'Question Papers — assemble papers by subject from the bank.',
          'Timetable Setup → mode Online + link paper; students attempt under Academic → Online Exams.',
          'One attempt per schedule; windowed by start/end time; scores hidden until results publish.',
        ],
      },
      {
        heading: 'Online Evaluation & results',
        points: [
          'Online Evaluation — select online schedule and submitted student; adjust marks (objective auto-graded).',
          'Exam Result — Assign Marks for offline/internal entry; syncs with online totals.',
          'Publish exam — students/parents see results; Grade scale and Top Performers use published data.',
        ],
      },
      {
        heading: 'Portal access',
        points: [
          'Students: exam timetable, Online Exams attempts, published results.',
          'Parents/Guardians: timetable and published results (no online attempt).',
          'Teachers: marks, papers, online evaluation for assigned classes/subjects.',
        ],
      },
    ],
    workflow: [
      'Exam Types → create Exam (optionally enable Internal Assessment).',
      'Exams → Manage Timetable — set Offline/Online per subject; attach paper for Online.',
      'For Online: build Question Bank → Question Paper → students attempt Online Exams.',
      'Staff: Online Evaluation and/or Exam Result Assign Marks; then Publish.',
      'Review Grade configuration and Top Performers; portals show published results.',
    ],
    roles: [
      'Headmaster / Admin (full setup & publish)',
      'Teachers (marks, papers, online evaluation)',
      'Students (timetable, online attempts, results)',
      'Parents (timetable & published results)',
    ],
  },
  {
    id: 'assemblies-management',
    title: 'Assemblies Management',
    icon: '📢',
    accent: '#f97316',
    summary:
      'Schedule school assemblies, target houses/groups/classes, mark attendance, award house points, and report.',
    purpose:
      'Plan morning assemblies and special gatherings with audience targeting (all students, class, house, group, or individuals), agenda items, attendance (Present/Absent/Late/Excused), house-point awards linked to Student Groups, soft-delete trash, and summary reports.',
    features: [
      {
        heading: 'All Assemblies',
        points: [
          'Academic → Assemblies → All Assemblies — list with status filter, search, export.',
          'Create Assembly — title, date, time, venue, audience, description.',
          'Statuses: Scheduled → Ongoing → Completed → Cancelled.',
        ],
      },
      {
        heading: 'Audience & agenda',
        points: [
          'Audience: All Students, Class, Class Section, House, Group, Student.',
          'Houses and Groups pull from Student Groups module.',
          'Detail page — manage agenda items, change status, award house points.',
        ],
      },
      {
        heading: 'Attendance',
        points: [
          'Open Attendance from an assembly — roster from targeted audience.',
          'Mark Present, Absent, Late, or Excused; bulk apply; save.',
        ],
      },
      {
        heading: 'Reports & trash',
        points: [
          'Reports — Summary, Attendance aggregates, House Points by year.',
          'Trashed Assemblies — soft-delete and restore (admin).',
          'Notifications when assembly is scheduled or cancelled.',
        ],
      },
    ],
    workflow: [
      'Create Student Groups / Houses if targeting by house or group.',
      'Academic → Assemblies → Create Assembly with audience and time.',
      'On the day: set Ongoing, mark attendance, add agenda notes.',
      'Award house points if needed; set Completed; use Reports for review.',
    ],
    roles: [
      'Headmaster / Admin (full manage + trash)',
      'Teachers (create, attendance, agenda)',
      'Students / Parents (view targeted assemblies & reports)',
    ],
  },
  {
    id: 'student-groups',
    title: 'Student Groups',
    icon: '🚩',
    accent: '#14b8a6',
    summary:
      'Houses, clubs, teams, and councils — members, roles, points ledger, and assembly targeting.',
    purpose:
      'Organise students into Houses, Clubs, Teams, Councils, or Other groups. Assign members with roles (Member, Captain, Prefect), track points, link tutors conceptually, and use groups as assembly audiences and house-point sources.',
    features: [
      {
        heading: 'Group catalog',
        points: [
          'Academic → Student Groups — create House, Club, Team, Council, or Other.',
          'Fields: name, type, color, captain label, description, active flag.',
          'Filter by type; soft-delete to Trashed with restore.',
        ],
      },
      {
        heading: 'Members & bulk assign',
        points: [
          'Detail page — add single member or bulk assign by class/section (up to 500).',
          'Roles: Member, Captain, Vice Captain, Prefect, Custom.',
          'House-type groups sync to the student’s primary house on profile.',
        ],
      },
      {
        heading: 'Points ledger',
        points: [
          'Manual points with reason; sources include Assembly, Sports, Manual, Other.',
          'Assemblies can award points to a house/group from the assembly detail page.',
        ],
      },
      {
        heading: 'Integrations',
        points: [
          'Assemblies audience House/Group uses active memberships.',
          'Settings → Houses redirects here; student profile house picker uses House groups.',
        ],
      },
    ],
    workflow: [
      'Create groups (at least Houses) for the academic year.',
      'Bulk-assign students; set captains/roles.',
      'Use groups in Assemblies targeting and award points from assemblies or manually.',
      'Review points on the group detail ledger and Assemblies House Points report.',
    ],
    roles: [
      'Headmaster / Admin (create, members, points, trash)',
      'Teachers / Students / Parents (view groups)',
    ],
  },
  {
    id: 'timetable-management',
    title: 'Timetable Management',
    icon: '🗓️',
    accent: '#3b82f6',
    summary: 'Define time slots, build class timetables, section routines, and teacher schedules.',
    purpose:
      'Eliminate timetable conflicts by configuring school periods once, then assigning subjects and teachers to each class section — with views for admins, teachers, students, and parents.',
    features: [
      {
        heading: 'Time slot configuration',
        points: [
          'Define school periods (start time, end time, break labels) under Timetable → Time slots.',
          'Reusable slots applied across all classes.',
        ],
      },
      {
        heading: 'Class & section timetables',
        points: [
          'Create Timetable maps subjects and teachers to periods per class.',
          'Section Routine view for individual section weekly grid.',
          'Bulk save for fast timetable building.',
        ],
      },
      {
        heading: 'Teacher routine',
        points: [
          'Teacher Timetable shows each teacher’s weekly teaching periods.',
          'Teacher Dashboard displays today’s routine widget.',
        ],
      },
      {
        heading: 'Portal views',
        points: [
          'Students and parents see Time Table under Academic in their portal.',
          'Print-friendly weekly grid layout.',
        ],
      },
    ],
    workflow: [
      'Configure Time slots for the school day.',
      'Open Create Timetable, select class/section, assign subject + teacher per period.',
      'Review Section Routine and Teacher Routine for conflicts.',
      'Students and parents access timetable from their portal automatically.',
    ],
    roles: ['Headmaster / Admin', 'Teachers (view own routine)', 'Students & Parents (view class timetable)'],
  },
  {
    id: 'homework-assignments',
    title: 'Homework & Assignments',
    icon: '📚',
    accent: '#8b5cf6',
    summary: 'Assign class homework, track submissions, and give grades — visible to students and parents.',
    purpose:
      'Let teachers publish homework with due dates and instructions, track who submitted, and grade work — while students and parents stay informed through their portals.',
    features: [
      {
        heading: 'Homework creation',
        points: [
          'Create homework by class, section, and subject with assign date and due date.',
          'Attach instructions and homework type (written, project, reading, etc.).',
          'Edit or delete assignments before the due date.',
        ],
      },
      {
        heading: 'Tracking & grading',
        points: [
          'List view with filters by class, subject, and date range.',
          'Submission status per student on homework detail page.',
          'Grade and return feedback on submitted work.',
        ],
      },
      {
        heading: 'Export & print',
        points: [
          'Export homework list to Excel or PDF for record keeping.',
          'Print class homework register for offline reference.',
        ],
      },
      {
        heading: 'Portal access',
        points: [
          'Students view assigned homework under Academic → Homework.',
          'Parents see child’s homework list and due dates in Parent Portal.',
        ],
      },
    ],
    workflow: [
      'Teacher opens Academic → Home Work.',
      'Click Add, select class, section, subject, due date, and instructions.',
      'Students view homework in their portal; teacher tracks submissions.',
      'Grade completed work and export records if needed.',
    ],
    roles: ['Teachers (create & grade)', 'Students (view & submit)', 'Parents (monitor child homework)'],
  },
  {
    id: 'meetings-management',
    title: 'Meetings Management',
    icon: '👥',
    accent: '#0ea5e9',
    summary:
      'Schedule PTM, staff, and department meetings — target audiences, record conclusions, assign follow-up tasks, and notify participants.',
    purpose:
      'SkoolWeb Meetings helps schools plan and communicate formal gatherings — parent–teacher meetings, staff briefings, department reviews, and student assemblies. Administrators and teachers schedule meetings with precise audience targeting, track status through completion, document conclusions, and assign actionable tasks to students or staff — with automatic notifications to everyone involved.',
    features: [
      {
        heading: 'All Meetings & My Meetings',
        points: [
          'Academic → Meetings → All Meetings — school-wide list with search, category, status, and audience filters.',
          'My Meetings — meetings created by the logged-in user (teachers track their own PTMs and class meetings).',
          'Create Meeting button on both pages (role-dependent); export list to Excel, PDF, or print.',
          'Click a meeting title to open the detail page; admins can delete meetings from All Meetings.',
        ],
      },
      {
        heading: 'Scheduling & audience targeting',
        points: [
          'Categories: Parent Teacher, Staff, Department, Student, Management, Teacher, General, Other.',
          'Multi-select categories and audience types per meeting (e.g. Class + Section, All Students, All Staff).',
          'Target specific classes, sections, students, departments, designations, staff, or custom selections.',
          'Set meeting date (no past dates), start/end time, venue, and description.',
          'Teachers have restricted categories — cannot create Staff, Department, Management, or Teacher-only meetings.',
        ],
      },
      {
        heading: 'Meeting detail & status',
        points: [
          'Detail page shows category, audience, date, time, venue, status badge, and target list.',
          'Status workflow: Scheduled → Ongoing → Completed → Cancelled (dropdown on detail page).',
          'Meet Conclusions — record minutes and decisions; editable by meeting creator or admin.',
          'Edit meeting — update schedule, audience, or targets via the same create form.',
        ],
      },
      {
        heading: 'Tasks & follow-up',
        points: [
          'Assign tasks from meeting detail — title, description, due date, assignee (student or staff).',
          'Task statuses: Pending, In Progress, Completed — updatable by admin, teacher, or assignee staff.',
          'Academic → Meetings → Assigned Tasks — cross-meeting task list with search and status filter.',
          'Parents and students see assigned tasks but cannot change status (read-only badge).',
        ],
      },
      {
        heading: 'Role-based visibility',
        points: [
          'Admins see all meetings; teachers see meetings they created plus those targeting them.',
          'Students, parents, and guardians see only meetings targeted to their class, section, or profile.',
          'Staff see meetings targeting their department, designation, or individual staff record.',
          'Portal sidebar: All Meetings + Assigned Tasks (no My Meetings for student/parent roles).',
        ],
      },
      {
        heading: 'Notifications',
        points: [
          'New meeting scheduled — notified to all users in the meeting audience.',
          'Meeting updated — audience receives an update notification.',
          'Meeting task assigned — assignee (and linked parent accounts for student tasks) are notified.',
          'Notification bell links directly to All Meetings or Assigned Tasks.',
        ],
      },
    ],
    workflow: [
      'Open Academic → Meetings → All Meetings (or My Meetings for teachers).',
      'Click Create Meeting — pick category, audience, targets, date, time, and venue.',
      'Participants receive a notification; they view the meeting under All Meetings.',
      'On meeting day, open detail → set status to Ongoing, then Completed.',
      'Record Meet Conclusions and assign follow-up tasks to students or staff.',
      'Track task completion under Assigned Tasks until all items are done.',
    ],
    roles: [
      'Headmaster / Admin (full create, edit, delete, task assign)',
      'Teachers (create & manage own meetings, assign tasks)',
      'Students, Parents & Guardians (view targeted meetings & tasks)',
      'Staff (view staff-targeted meetings & own tasks)',
    ],
  },
  {
    id: 'payroll-management',
    title: 'Payroll Management',
    icon: '💼',
    accent: '#10b981',
    summary:
      'Salary components, staff assignments, monthly payroll generation, payslips, and payment status tracking.',
    purpose:
      'SkoolWeb Payroll helps schools manage staff compensation end-to-end — define reusable salary components (earnings and deductions), assign basic salary and components to each teacher or staff member, generate monthly payslips in bulk, review net pay, mark salaries as paid, and let staff view and print their own salary slips securely.',
    features: [
      {
        heading: 'Salary components catalog',
        points: [
          'HRM → Payroll → Manage Components — define school-wide earnings and deductions.',
          'Component types: Earning (Allowance) and Deduction — e.g. HRA, Transport, PF, Tax.',
          'Add, edit, or deactivate components; reused when assigning salary to each staff member.',
        ],
      },
      {
        heading: 'Staff & teacher salary setup',
        points: [
          'People → Teachers → Add/Edit Teacher — Payroll section with basic salary, bank details, and component amounts.',
          'HRM → Staffs → Add/Edit Staff — basic salary, EPF, PAN, contract type, shift, and bank account.',
          'Active salary assignment must exist before payroll can be generated for a staff member.',
        ],
      },
      {
        heading: 'Monthly payroll generation',
        points: [
          'HRM → Payroll → Generate Payroll — pick month and year.',
          'System creates Draft payslips for all active staff with valid salary assignments.',
          'Skips staff who already have a payslip for that month; calculates gross and net from components.',
        ],
      },
      {
        heading: 'Payroll list & filters',
        points: [
          'Table columns: ID, Name, Month, Department, Designation, Phone, Amount, Status.',
          'Filter by staff name, month, and year; date range picker for period review.',
          'Export to Excel, PDF, or print; refresh to reload after generation.',
        ],
      },
      {
        heading: 'Payslips & salary slips',
        points: [
          'View Payslip opens a printable Salary Slip with earnings and deductions breakdown.',
          'Shows employee details, department, designation, basic salary, allowances, deductions, and net pay.',
          'Download PDF or print directly from the payslip page.',
        ],
      },
      {
        heading: 'Payment status & bulk actions',
        points: [
          'Status badges: Draft (pending payment) and Paid (settled).',
          'Mark Paid per row after bank transfer; Revert to Draft if entered incorrectly.',
          'Select multiple rows → bulk Mark Selected Paid or Delete Selected (unpaid only).',
          'Paid payslips cannot be deleted — protects payroll audit trail.',
        ],
      },
      {
        heading: 'Staff self-service & security',
        points: [
          'Teachers view own payslip history under People → Teachers → Teacher Salary.',
          'Non-admin staff see only their own payslips — enforced on server.',
          'Only Headmaster / Admin can generate payroll, delete drafts, and bulk-update status.',
        ],
      },
    ],
    workflow: [
      'HRM → Payroll → Manage Components — create earnings and deductions.',
      'Add or edit each teacher/staff record with basic salary and assigned components.',
      'HRM → Payroll → Generate Payroll — select month and year.',
      'Review Draft payslips in the list; open View Payslip to verify amounts.',
      'Mark individual or bulk payslips as Paid after salary disbursement.',
      'Staff view and print their own salary slips from the teacher/staff profile.',
    ],
    roles: [
      'Headmaster / Admin (generate, approve, delete, export)',
      'Administrative / HR staff (day-to-day payroll processing)',
      'Teachers & Staff (view own payslips only)',
    ],
  },
  {
    id: 'parent-portal',
    title: 'Parent Portal',
    icon: '👨‍👩‍👧',
    accent: '#ec4899',
    summary: 'Dedicated portal for parents to monitor child progress, fees, attendance, exams, and notices.',
    purpose:
      'Keep parents engaged without phone calls — they log in once and see everything about their linked children: attendance, fees, homework, exam results, timetable, and school announcements.',
    features: [
      {
        heading: 'Parent dashboard',
        points: [
          'Overview of linked children with attendance %, pending fees, and new notices.',
          'Child switcher when parent has more than one student enrolled.',
        ],
      },
      {
        heading: 'Academic visibility',
        points: [
          'Child Profile, Subjects, Syllabus, Time Table, and Homework.',
          'Leave & Attendance — view history and apply for leave on behalf of child.',
          'Exam Timetable and Exam Results when published by school.',
        ],
      },
      {
        heading: 'Fees & communication',
        points: [
          'Fees screen shows dues, payments, and receipt history.',
          'Notice Board and Events for PTM schedules, holidays, and announcements.',
        ],
      },
      {
        heading: 'Account & security',
        points: [
          'Profile page to update contact details and change password.',
          'Role-scoped data — parents only see their own linked children.',
        ],
      },
    ],
    workflow: [
      'School creates parent account linked to student during admission.',
      'Parent logs in with institute number and credentials.',
      'Dashboard shows summary; Academic menu opens child-specific records.',
      'Apply for leave or check fees without visiting the school office.',
    ],
    roles: ['Parents / Guardians'],
  },
  {
    id: 'teacher-portal',
    title: 'Teacher Portal',
    icon: '👩‍🏫',
    accent: '#06b6d4',
    summary: 'Focused workspace for teachers — dashboard, attendance, timetable, exams, and class students.',
    purpose:
      'Teachers get a streamlined interface without admin clutter: mark attendance, view routine, manage class students, enter exam marks, publish homework, and read school notices.',
    features: [
      {
        heading: 'Teacher dashboard',
        points: [
          'Class size, today’s attendance summary, and pending leave requests.',
          'Today’s teaching routine and upcoming events widget.',
          'Star students and performance highlights for assigned classes.',
        ],
      },
      {
        heading: 'Academic tools',
        points: [
          'Timetable, Home Work, Syllabus, Attendance, and Examinations.',
          'Mark student attendance for assigned classes only.',
          'Enter exam marks and view top performers.',
        ],
      },
      {
        heading: 'People access',
        points: [
          'Students, Parents, and Guardians lists scoped to teacher’s classes.',
          'Student Details, Student Leaves approval, and Bonafide generation.',
        ],
      },
      {
        heading: 'Communication',
        points: [
          'Notice Board and Events for school-wide updates.',
          'Student Attendance Report for class analytics.',
          'My Leave & Attendance for personal leave applications.',
        ],
      },
    ],
    workflow: [
      'Teacher logs in and lands on Teacher Dashboard.',
      'Check Today’s Routine before first period.',
      'Mark attendance under Academic → Attendance → Student Attendance.',
      'Enter homework or exam marks; review notices from Announcements.',
    ],
    roles: ['Teachers / Class teachers'],
  },
  {
    id: 'transport-management',
    title: 'Transport Management',
    icon: '🚌',
    accent: '#f97316',
    summary: 'Manage routes, vehicles, drivers, pickup points, student allocation, and transport fees.',
    purpose:
      'Run school transport operations digitally — define routes and stops, assign vehicles and drivers, allocate students to routes, and optionally link transport fees to billing.',
    features: [
      {
        heading: 'Route & pickup setup',
        points: [
          'Create transport routes with named stops and pickup points.',
          'Define pickup times and route sequence.',
        ],
      },
      {
        heading: 'Fleet management',
        points: [
          'Vehicle registry with capacity, registration, and status.',
          'Transport Staff (drivers) linked to vehicles.',
          'Assign Vehicle to route with driver allocation.',
        ],
      },
      {
        heading: 'Student allocation',
        points: [
          'Transport Allocation assigns students to route and pickup point.',
          'View which students are on each route for daily planning.',
        ],
      },
      {
        heading: 'Transport fees',
        points: [
          'Transport Fees configuration linked to fee collection module.',
          'Include transport charges in student fee statements.',
        ],
      },
    ],
    workflow: [
      'Add Pickup Points and Routes under Management → Transport.',
      'Register Vehicles and Transport Staff (drivers).',
      'Assign Vehicle to each route.',
      'Allocate students via Transport Allocation; set transport fees if applicable.',
    ],
    roles: ['Headmaster / Admin', 'Transport coordinator'],
  },
  {
    id: 'library-management',
    title: 'Library Management',
    icon: '📖',
    accent: '#14b8a6',
    summary: 'Full library catalog, members, issue/return, reservations, and policies.',
    purpose:
      'Digitize the school library — catalog books with copies, register members (students/staff), issue and return books, manage reservations, and enforce lending policies.',
    features: [
      {
        heading: 'Catalog & copies',
        points: [
          'Books master with title, author, ISBN, category, and rack location.',
          'Track individual book copies and availability status.',
          'Library Categories for organized browsing.',
        ],
      },
      {
        heading: 'Members & policies',
        points: [
          'Register Library Members from students or staff.',
          'Policies define max books, loan period, and fine rules.',
        ],
      },
      {
        heading: 'Issue & return',
        points: [
          'Issue Book workflow selects member and available copy.',
          'Return screen marks book returned and clears member record.',
          'Reservations queue for books currently on loan.',
        ],
      },
    ],
    workflow: [
      'Add Categories and Books with copy count.',
      'Register students as Library Members.',
      'Issue Book to member; system tracks due date.',
      'Process Return when book comes back; manage Reservations for waiting members.',
    ],
    roles: ['Headmaster / Admin', 'Librarian'],
  },
  {
    id: 'hostel-management',
    title: 'Hostel Management',
    icon: '🏠',
    accent: '#6366f1',
    summary: 'Hostel buildings, floors, rooms, beds, and student bed assignments.',
    purpose:
      'Manage boarding facilities — define hostel structure from building to individual bed, assign students to beds, track occupancy, and handle checkout when students leave the hostel.',
    features: [
      {
        heading: 'Hostel structure',
        points: [
          'Hostel List for buildings/campuses.',
          'Hostel Floors, Room Types, Hostel Rooms, and Hostel Beds hierarchy.',
          'Visual bed inventory with available vs. occupied status.',
        ],
      },
      {
        heading: 'Student assignment',
        points: [
          'Hostel Assignments links a student to a specific bed.',
          'Checkout workflow when student vacates the hostel.',
          'Cancel or change assignment when rooms are swapped.',
        ],
      },
      {
        heading: 'Occupancy overview',
        points: [
          'Filter beds by hostel, floor, and availability.',
          'Quick view of which students occupy which rooms.',
        ],
      },
    ],
    workflow: [
      'Create Hostel → Floors → Room Types → Rooms → Beds.',
      'Open Hostel Assignments and select student + available bed.',
      'Monitor occupancy from bed list filters.',
      'Checkout student when they leave the hostel.',
    ],
    roles: ['Headmaster / Admin', 'Hostel warden / Administrative staff'],
  },
];
